import api from 'services/api';
import { array, enforce, nullable, num, object, runSchema, str, type FieldParser } from 'validation';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { schedulingWindowReferenceSchema, schedulingWindowScheduleSchema, type SchedulingWindowReference, type SchedulingWindowSchedule } from './types';

/**
 * A single scheduling window with its rules and links.
 *
 * Shared by true IDENTITY across four routes — `GET /scheduling_windows/:id`,
 * `GET /scheduling_windows/:zone_name/:window_name`, and the bodies POST and PUT
 * echo back — because all four serialize through the one `windowDetailsSQL()`
 * builder and then attach `folder_path` via the one `buildFolderPath` helper. So
 * `postSchedulingWindow` / `putSchedulingWindow` import this type and schema
 * rather than redeclaring them. The list read does NOT (see
 * `getSchedulingWindows`).
 *
 * `id` is the backend's `"zone_name|name"` display key, not a numeric id; the
 * numeric identity is `scheduling_window_id`.
 *
 * The window's own column set is exactly these eight columns plus the derived
 * `id` / `schedules` / `references` / `folder_path` — there is no `is_calendar`,
 * `day_rollover`, `min_open_duration_ms` or `exclusions` anywhere behind this
 * route.
 */
export interface SchedulingWindow {
  readonly scheduling_window_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly timezone: string;
  readonly zone_name: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly folder_id: number | null;
  readonly folder_path: FolderPath | null;
  readonly id: string | null;
  readonly schedules: readonly SchedulingWindowSchedule[];
  readonly references: readonly SchedulingWindowReference[];
}

export const schedulingWindowSchema: FieldParser<SchedulingWindow> = object<SchedulingWindow>({
  scheduling_window_id: num(),
  name: str(),
  description: nullable(str()),
  // NOT NULL DEFAULT 'UTC' on the window itself (unlike a schedule's override).
  timezone: str(),
  // Nullable because `scheduling_windows.zone_id` is nullable and the SQL
  // LEFT JOINs zones; `id` concatenates from it, so it is nullable for the same
  // reason.
  zone_name: nullable(str()),
  created_at: str(),
  updated_at: str(),
  folder_id: nullable(num()),
  folder_path: nullable(folderPathSchema),
  id: nullable(str()),
  schedules: array(schedulingWindowScheduleSchema),
  references: array(schedulingWindowReferenceSchema),
});

/**
 * GET `/scheduling_windows/:id`. Returns `null` when the window cannot be
 * used — a transport error, a 404, a structural failure, or a drifted identity —
 * so a caller surfaces "not found" instead of opening a form on a degraded
 * record. Rules and links whose own identity drifted are dropped rather than
 * failing the whole window.
 */
export const getSchedulingWindow = async (id: number): Promise<SchedulingWindow | null> => {
  try {
    const res = await api.get(`/scheduling_windows/${id}`);
    const result = runSchema(schedulingWindowSchema, res.data, 'getSchedulingWindow');
    if (!result.ok) return null;
    return enforce(result.value, result.warnings, ['scheduling_window_id', 'references[*].scheduling_window_path']);
  } catch {
    return null;
  }
};
